import { useState, useRef, useEffect } from 'react';
import { Input, Tooltip } from "@heroui/react";
import { Check, X, Info } from "@phosphor-icons/react";
import { toast } from "react-toastify";

interface InlineTitleEditorProps {
    title: string;
    onSave: (newTitle: string) => Promise<void>;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    maxLength?: number;
    showCharacterLimit?: boolean;
    isProjectPart?: boolean;
}

export const InlineTitleEditor = ({ 
    title, 
    onSave, 
    className = "", 
    placeholder = "Voer titel in...",
    disabled = false,
    maxLength = 100,
    showCharacterLimit = false,
    isProjectPart = false
}: InlineTitleEditorProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    useEffect(() => {
        setEditValue(title);
    }, [title]);

    const handleSave = async () => {
        const trimmedValue = editValue.trim();
        
        if (!trimmedValue) {
            toast.error("Titel mag niet leeg zijn");
            return;
        }

        if (trimmedValue === title) {
            setIsEditing(false);
            return;
        }

        try {
            setIsSaving(true);
            await onSave(trimmedValue);
            setIsEditing(false);
            toast.success("Titel succesvol bijgewerkt");
        } catch (error) {
            console.error('Error saving title:', error);
            toast.error("Fout bij opslaan titel");
            setEditValue(title); // Reset to original value
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditValue(title);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };

    if (isEditing) {
        const remainingChars = maxLength - editValue.length;
        const isNearLimit = remainingChars <= 10;
        const isOverLimit = remainingChars < 0;

        return (
            <div 
                className="flex flex-col gap-1 flex-1 min-w-0" 
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-1 flex-1 min-w-0">
                    <Input
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        maxLength={maxLength}
                        disabled={isSaving}
                        size="sm"
                        classNames={{
                            input: className?.includes('text-lg') ? "text-lg font-semibold" : "text-sm font-medium",
                            inputWrapper: `border-moss-primary/30 focus-within:border-moss-primary ${className?.includes('text-lg') ? 'h-10 min-h-10' : 'h-8 min-h-8'} ${isOverLimit ? 'border-brick-primary/50 focus-within:border-brick-primary' : ''}`,
                            base: "min-w-0 flex-1"
                        }}
                    />
                    <div className="flex items-center gap-0.5 shrink-0">
                        <button
                            onClick={handleSave}
                            disabled={isSaving || !editValue.trim() || isOverLimit}
                            className="p-1 rounded-sm bg-status-good text-white hover:bg-status-good/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Opslaan (Enter)"
                        >
                            <Check size={12} weight="bold" />
                        </button>
                        <button
                            onClick={handleCancel}
                            disabled={isSaving}
                            className="p-1 rounded-sm bg-neutral-400 text-white hover:bg-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Annuleren (Esc)"
                        >
                            <X size={12} weight="bold" />
                        </button>
                    </div>
                </div>
                {showCharacterLimit && (
                    <div className="flex items-center gap-1 text-xs">
                        <span className={`${isOverLimit ? 'text-brick-primary' : isNearLimit ? 'text-yellow-600' : 'text-neutral-500'}`}>
                            {editValue.length}/{maxLength} tekens
                        </span>
                        <Tooltip content={`Titel beperkt tot ${maxLength} tekens vanwege SharePoint pad beperkingen. ${isProjectPart ? 'Projectonderdelen' : 'Projecten'} hebben deze limiet om problemen met te lange bestandspaden te voorkomen.`}>
                            <Info size={12} className="text-neutral-400 hover:text-neutral-600 cursor-help" />
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    }

    return (
        <span 
            className={`${className || 'text-sm font-medium'} text-neutral-primary hover:text-moss-primary transition-colors cursor-pointer hover:underline truncate min-w-0 flex-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) setIsEditing(true);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title={disabled ? 'Bewerken uitgeschakeld' : `${title} - Klik om te bewerken`}
        >
            {title}
        </span>
    );
};
